import React, { useEffect } from "react";
import { FileText, CheckCircle, Clock, Archive } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchTemplates,
  TemplatesResponse,
} from "../api/endpoints/fetchTemplates";
import { useAuth } from "../contexts/AuthContext";

export function AdminDashboardCardsSection() {
  const { user, isAdmin } = useAuth();

  // fetchTemplates now handles online/offline logic internally
  // IMPORTANT: Use the same query key as TemplatesList to share cache
  const { data, isLoading, error } = useQuery<TemplatesResponse>({
    queryKey: ["templates", isAdmin ? "admin" : "manager", user?.email ?? ""],
    queryFn: () => fetchTemplates(user?.email || ""),
    enabled: !!user?.email,
    retry: false, // Don't retry on failure
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

  // Log errors for debugging
  useEffect(() => {
    if (error) {
      console.error("Error fetching templates:", error);
    }
  }, [error]);

  const totalForms = React.useMemo(() => data?.data?.length ?? 0, [data?.data]);
  // Per request: completed should be 0 for now
  const completed = 0;
  // Per request: all current forms are in issue state => treat all as pending
  const pending = totalForms;
  const overdue = 0;
  const completionRate = 0;

  return (
    <div className="grid grid-cols-4 gap-4 mb-6">
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Total Forms</p>
            <p className="text-2xl font-bold text-gray-900">{totalForms}</p>
          </div>
          <FileText className="w-8 h-8 text-gray-400" />
        </div>
      </div>
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Completed</p>
            <p className="text-2xl font-bold text-green-600">{completed}</p>
          </div>
          <CheckCircle className="w-8 h-8 text-green-400" />
        </div>
      </div>
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Pending</p>
            <p className="text-2xl font-bold text-amber-600">{pending}</p>
          </div>
          <Clock className="w-8 h-8 text-amber-400" />
        </div>
      </div>
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Archived</p>
            <p className="text-2xl font-bold text-gray-600">0</p>
          </div>
          <Archive className="w-8 h-8 text-gray-400" />
        </div>
      </div>
    </div>
  );
}
